import CheckIcon from 'assets/svg/app/check-fill.svg'
import DeprecatedXIcon from 'assets/svg/app/deprecated-x.svg'
import FlagIcon from 'assets/svg/app/flag-circle-rounded.svg'
import SettingsIcon from 'assets/svg/app/settings-filled.svg'
import ShieldIcon from 'assets/svg/app/shield-rounded.svg'
import BaseModal from 'components/BaseModal'
import { TransactionButton } from 'components/Button/Button'
import ProgressIndicator from 'components/ProgressIndicator'
import Spacer from 'components/Spacer'
import { Body, Heading } from 'components/Text'
import { FlexDivCol, FlexDivColCentered, FlexDivRowCentered } from 'components/layout/flex'
import { EXTERNAL_LINKS } from 'constants/links'
import { type FC, type ReactNode, memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { setOpenModal } from 'state/app/reducer'
import { selectIsAccountCreated, selectIsAccountFailed } from 'state/futures/selectors'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import styled from 'styled-components'
import media from 'styles/media'

type Step = {
	id: string
	message: string
	icon: ReactNode
	status: 'pending' | 'current' | 'completed'
}

type Props = {
	onDismiss(): void
}

const INITIAL_STEPS: Step[] = [
	{
		id: 'init',
		message: 'Creating Account',
		icon: <CheckIcon height={14} width={14} />,
		status: 'pending',
	},
	{
		id: 'setup',
		message: 'Setting up Account',
		icon: <SettingsIcon height={14} width={14} />,
		status: 'pending',
	},
	{
		id: 'permissions',
		message: 'Setting up Permissions',
		icon: <ShieldIcon height={14} width={14} />,
		status: 'pending',
	},
	{
		id: 'finalize',
		message: 'Finalizing Deployment',
		icon: <FlagIcon height={14} width={14} />,
		status: 'pending',
	},
]

const AbstractionAccountSetupModal: FC<Props> = memo(({ onDismiss }) => {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()
	const isAccountCreated = useAppSelector(selectIsAccountCreated)
	const isAccountFailed = useAppSelector(selectIsAccountFailed)
	const [currentStepIndex, setCurrentStepIndex] = useState(0)
	const [steps, setSteps] = useState(INITIAL_STEPS)
	const isAccountCreating = useMemo(
		() => !isAccountCreated && !isAccountFailed,
		[isAccountCreated, isAccountFailed]
	)

	useEffect(() => {
		if (currentStepIndex === 0 && steps[0].status === 'pending') {
			setSteps((prevSteps) => {
				const newSteps = [...prevSteps]
				newSteps[0] = { ...newSteps[0], status: 'current' }
				return newSteps
			})
		}

		if (isAccountCreated || isAccountFailed) return

		const timer = setTimeout(() => {
			if (currentStepIndex < steps.length - 1) {
				setSteps((prevSteps) => {
					const newSteps = [...prevSteps]

					newSteps[currentStepIndex] = {
						...newSteps[currentStepIndex],
						status: 'completed',
					}

					newSteps[currentStepIndex + 1] = {
						...newSteps[currentStepIndex + 1],
						status: 'current',
					}
					return newSteps
				})

				setCurrentStepIndex((prev) => prev + 1)
			} else if (currentStepIndex === steps.length - 1) {
				// Mark the last step as completed
				setSteps((prevSteps) => {
					const newSteps = [...prevSteps]
					newSteps[currentStepIndex] = {
						...newSteps[currentStepIndex],
						status: 'completed',
					}
					return newSteps
				})
			}
		}, 2500)
		return () => clearTimeout(timer)
	}, [currentStepIndex, isAccountCreated, isAccountFailed, steps.length, steps[0].status])

	const handleDepositFunds = useCallback(
		() => dispatch(setOpenModal('futures_deposit_funds')),
		[dispatch]
	)

	const handleOpenDiscord = useCallback(() => {
		window.open(EXTERNAL_LINKS.Social.Discord, '_blank')
	}, [])

	return (
		<StyledBaseModal title="" isOpen onDismiss={onDismiss} transparentOverlay={false}>
			<Container>
				<ProgressIndicator currentStep={2} totalSteps={3} />
				<Heading variant="h5">{t('account.create-account-tracker.title')}</Heading>
				<Divider height={1} divider />
				{isAccountCreated ? (
					<FlexDivColCentered rowGap="12px">
						<StyledCircleCheckIcon height={60} width={60} />
						<FlexDivColCentered rowGap="8px">
							<Heading variant="h6">{t('account.create-account-tracker.account-created')}</Heading>
							<Body color="secondary">{t('account.create-account-tracker.completed-copy-aa')}</Body>
						</FlexDivColCentered>
					</FlexDivColCentered>
				) : isAccountFailed ? (
					<FlexDivRowCentered>
						<DeprecatedXIcon height={90} width={90} />
					</FlexDivRowCentered>
				) : (
					<FlexDivCol rowGap="24px">
						{steps.map(({ id, message, icon, status }) => (
							<AnimationRow key={id} columnGap="8px">
								<IconContainer $status={status}>{icon}</IconContainer>
								<Body
									weight="semiBold"
									color={
										status === 'pending'
											? 'secondary'
											: status === 'completed'
												? 'primary'
												: 'preview'
									}
									size="large"
								>
									{message}
								</Body>
							</AnimationRow>
						))}
					</FlexDivCol>
				)}
				{isAccountCreating && (
					<MessageContainer>{t('account.create-account-tracker.submitting-copy')}</MessageContainer>
				)}
				{isAccountFailed && <Body>{t('account.create-account-tracker.account-error-copy')}</Body>}
				{isAccountCreated && (
					<TransactionButton
						fullWidth
						variant="solid-highlight"
						disabled={isAccountCreating}
						loading={isAccountCreating}
						onClick={handleDepositFunds}
					>
						{isAccountCreated
							? t('account.create-account-tracker.deposit-funds')
							: t('account.create-account-tracker.try-again')}
					</TransactionButton>
				)}
				{isAccountFailed && (
					<TransactionButton
						fullWidth
						disabled={isAccountCreating}
						loading={isAccountCreating}
						onClick={isAccountCreated ? onDismiss : handleOpenDiscord}
					>
						{isAccountCreated
							? t('account.create-account-tracker.dismiss')
							: t('account.create-account-tracker.contact-support')}
					</TransactionButton>
				)}
			</Container>
		</StyledBaseModal>
	)
})

const IconContainer = styled.div<{ $status: 'pending' | 'completed' | 'current' }>`
	width: 24px;
	height: 24px;
	border-radius: 50%;
	border: ${(props) =>
		props.$status === 'pending'
			? props.theme.colors.selectedTheme.newTheme.border.style
			: props.$status === 'completed'
				? props.theme.colors.selectedTheme.newTheme.border.green
				: 'transparent'};
	display: flex;
	align-items: center;
	justify-content: center;
	position: relative;
	overflow: ${(props) => (props.$status === 'current' ? 'visible' : 'hidden')};

	&::before {
		content: '';
		position: absolute;
		top: -1px;
		left: -1px;
		width: calc(100% + 2px);
		height: calc(100% + 2px);
		border-radius: 50%;
		border: 1px solid transparent;
		border-top-color: ${(props) => props.theme.colors.selectedTheme.newTheme.text.green};
		animation: ${(props) => (props.$status === 'current' ? 'spin 1.5s linear forwards' : 'none')};
		box-sizing: border-box;
	}

	@keyframes spin {
		0% {
			transform: rotate(0deg);
			border-top-color: ${(props) => props.theme.colors.selectedTheme.newTheme.text.preview};
			border-right-color: transparent;
			border-bottom-color: transparent;
			border-left-color: transparent;
		}
		25% {
			border-top-color: ${(props) => props.theme.colors.selectedTheme.newTheme.text.green};
			border-right-color: ${(props) => props.theme.colors.selectedTheme.newTheme.text.preview};
			border-bottom-color: transparent;
			border-left-color: transparent;
		}
		50% {
			border-top-color: ${(props) => props.theme.colors.selectedTheme.newTheme.text.green};
			border-right-color: ${(props) => props.theme.colors.selectedTheme.newTheme.text.green};
			border-bottom-color: ${(props) => props.theme.colors.selectedTheme.newTheme.text.preview};
			border-left-color: transparent;
		}
		75% {
			border-top-color: ${(props) => props.theme.colors.selectedTheme.newTheme.text.green};
			border-right-color: ${(props) => props.theme.colors.selectedTheme.newTheme.text.green};
			border-bottom-color: ${(props) => props.theme.colors.selectedTheme.newTheme.text.green};
			border-left-color: ${(props) => props.theme.colors.selectedTheme.newTheme.text.preview};
		}
		100% {
			transform: rotate(360deg);
			border-color: ${(props) => props.theme.colors.selectedTheme.newTheme.text.green};
		}
	}

	svg {
		path {
			fill: ${(props) =>
				props.$status === 'pending'
					? props.theme.colors.selectedTheme.newTheme.text.secondary
					: props.$status === 'completed'
						? props.theme.colors.selectedTheme.newTheme.text.green
						: props.theme.colors.selectedTheme.newTheme.text.preview};
		}
	}
`

const MessageContainer = styled.div`
	color: ${(props) => props.theme.colors.selectedTheme.newTheme.text.primary};
	background: ${(props) => props.theme.colors.selectedTheme.newTheme.containers.highlight.background};
	flex: none;
	order: 0;
	flex-grow: 0;
	text-align: center;
	font-size: 12px;
	padding: 10px 20px;
	border-radius: 4px;
	cursor: default;
	white-space: pre-line;
`

const Divider = styled(Spacer)`
	width: 100%;
`

const Container = styled(FlexDivCol)`
	row-gap: 30px;
	padding: 10px;
`

const AnimationRow = styled(FlexDivRowCentered)`
	justify-content: flex-start;
`

const StyledCircleCheckIcon = styled(CheckIcon)`
	path {
		fill: ${(props) => props.theme.colors.green};
	}
`

const StyledBaseModal = styled(BaseModal)`
	[data-reach-dialog-content] {
		max-width: 550px;
		margin-top: 250px;
	}

	${media.lessThan('md')`
		[data-reach-dialog-content] {
			max-width: 100%;
		}
	`}
`

export default AbstractionAccountSetupModal
